import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import BrandedHero from "@/components/BrandedHero";
import StatusFlag, { STATUS_COLORS } from "@/components/StatusFlag";
import PinToSweeperButton from "@/components/PinToSweeperButton";
import { PushPin } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * AdminJobsV2 — CP-style Work Orders list. Kanban board / list toggle, colored
 * status counter strip, filter row, and a card grid or dense list of WOs.
 *
 * Independent from AdminJobs (V1). V1 keeps its bulk ops, CSV import, legacy
 * filters, group-by-zip, etc. Click "Use Classic" to go back to V1.
 */

const STATUS_ORDER = [
  { k: "new",         label: "New" },
  { k: "pool",        label: "Pool" },
  { k: "assigned",    label: "Assigned" },
  { k: "in_progress", label: "Active" },
  { k: "follow_up",   label: "Follow-up" },
  { k: "completed",   label: "Completed" },
];

export default function AdminJobsV2() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const view = searchParams.get("view") || "board";      // board | list
  const statusFilter = searchParams.get("status") || "";
  const companyFilter = searchParams.get("company") || "";
  const techFilter = searchParams.get("tech") || "";
  const portfolioFilter = searchParams.get("portfolio") || "";
  const priorityFilter = searchParams.get("priority") || "";  // urgent | critical | life_safety | ""
  const propertyFilter = searchParams.get("property") || "";  // address string — set when arriving from a Property page

  const loadJobs = () => {
    // If a property filter is on, ask the backend to scope — otherwise
    // we might miss WOs that fall past the 500-item cap.
    const params = { limit: 500 };
    if (searchParams.get("property")) params.property = searchParams.get("property");
    return api.get("/jobs", { params }).then(r => setJobs(r.data || []));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadJobs(),
      api.get("/assignable-users").then(r => setWorkers(r.data || [])).catch(() => setWorkers([])),
    ]).catch(() => setJobs([])).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyFilter]);   // refetch when the property scope changes so backend narrows too

  const matchesPriority = (j) => {
    if (!priorityFilter) return true;
    if (priorityFilter === "urgent")      return j.priority === "urgent" || j.priority === "critical";
    if (priorityFilter === "life_safety") return !!(j.habitability || j.life_safety);
    if (priorityFilter === "normal")      return !j.priority || j.priority === "normal" || j.priority === "medium";
    return j.priority === priorityFilter;
  };

  // Distinct companies (customer_name / landlord), techs, and portfolios,
  // computed off the loaded jobs so filter controls stay in sync with the
  // data. Portfolios also expose the brand color for the chip pill.
  const { companyOptions, techOptions, portfolioOptions, priorityCounts } = useMemo(() => {
    const co = new Map();
    const tc = new Map();
    const po = new Map(); // slug → { name, color, count }
    let urgent = 0, life = 0, normal = 0;
    for (const j of jobs) {
      const c = (j.customer_name || "").trim();
      if (c) co.set(c, (co.get(c) || 0) + 1);
      const t = (j.assigned_to_name || "").trim();
      if (t) tc.set(t, (tc.get(t) || 0) + 1);
      const slug = (j.portfolio_slug || "").trim();
      if (slug) {
        const prev = po.get(slug) || { name: j.portfolio_name || slug, color: j.portfolio_color || "#8B93A7", count: 0 };
        prev.count += 1;
        po.set(slug, prev);
      }
      if (j.priority === "urgent" || j.priority === "critical") urgent += 1;
      if (j.habitability || j.life_safety) life += 1;
      if (!j.priority || j.priority === "normal" || j.priority === "medium") normal += 1;
    }
    const sortByCount = (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]);
    return {
      companyOptions:   [...co.entries()].sort(sortByCount),
      techOptions:      [...tc.entries()].sort(sortByCount),
      portfolioOptions: [...po.entries()].sort((a, b) => b[1].count - a[1].count),
      priorityCounts:   { urgent, life_safety: life, normal },
    };
  }, [jobs]);

  // Client-side counts + filters
  const counts = useMemo(() => {
    const c = {};
    // Counts must respect Company/Tech/Portfolio/Priority filters so status
    // chips reflect the narrowed slice — otherwise picking "4S Bay" but
    // seeing "Assigned 101" for the whole tenant is confusing.
    const scoped = jobs.filter(j =>
      (!companyFilter   || (j.customer_name || "") === companyFilter) &&
      (!techFilter      || (j.assigned_to_name || "") === techFilter) &&
      (!portfolioFilter || (j.portfolio_slug  || "") === portfolioFilter) &&
      (!propertyFilter  || matchesPropertyAddress(j.property_address, propertyFilter)) &&
      matchesPriority(j)
    );
    for (const j of scoped) c[j.status] = (c[j.status] || 0) + 1;
    c._total = scoped.length;
    c._urgent = scoped.filter(j => j.priority === "urgent" || j.priority === "critical" || j.habitability).length;
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, companyFilter, techFilter, portfolioFilter, priorityFilter, propertyFilter]);

  const filtered = useMemo(() => {
    let list = jobs;
    if (propertyFilter)  list = list.filter(j => matchesPropertyAddress(j.property_address, propertyFilter));
    if (companyFilter)   list = list.filter(j => (j.customer_name || "") === companyFilter);
    if (techFilter)      list = list.filter(j => (j.assigned_to_name || "") === techFilter);
    if (portfolioFilter) list = list.filter(j => (j.portfolio_slug  || "") === portfolioFilter);
    if (priorityFilter)  list = list.filter(matchesPriority);
    if (statusFilter && statusFilter !== "all") {
      if (statusFilter === "open") {
        // "open" is a virtual meta-status used by the Property page's
        // "Open WOs" tile — treat it as any not-yet-closed WO.
        const closed = new Set(["completed", "complete", "canceled", "cancelled", "merged"]);
        list = list.filter(j => !closed.has((j.status || "").toLowerCase()));
      } else {
        list = list.filter(j => j.status === statusFilter);
      }
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(j =>
        (j.title || "").toLowerCase().includes(needle) ||
        (j.property_address || "").toLowerCase().includes(needle) ||
        (j.tenant_name || j.customer_name || "").toLowerCase().includes(needle) ||
        String(j.work_order_number || "").toLowerCase().includes(needle) ||
        (j.assigned_to_name || "").toLowerCase().includes(needle)
      );
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, statusFilter, companyFilter, techFilter, portfolioFilter, priorityFilter, propertyFilter, q]);

  const groupedByStatus = useMemo(() => {
    const groups = Object.fromEntries(STATUS_ORDER.map(s => [s.k, []]));
    for (const j of filtered) if (groups[j.status]) groups[j.status].push(j);
    return groups;
  }, [filtered]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val == null || val === "") next.delete(key);
    else next.set(key, val);
    setSearchParams(next, { replace: true });
  };

  return (
    <AdminShell>
      <BrandedHero
        breadcrumbs={[{ to: "/admin?v=v2", label: "← Dashboard" }, { label: "Work Orders" }]}
        eyebrow="Work Orders"
        title="Work Orders"
        subtitle={
          <>
            <b>{counts._total} total</b> · {counts.new || 0} new · {counts.pool || 0} pool · {counts.assigned || 0} assigned · {counts.in_progress || 0} active
            {counts._urgent > 0 && <> · <b className="text-white">🔥 {counts._urgent} urgent</b></>}
          </>
        }
        size="md"
        actions={
          <>
            <div className="inline-flex bg-white/15 backdrop-blur rounded-full p-1">
              {[{ k: "board", label: "Board", icon: "▦" }, { k: "list", label: "List", icon: "≡" }].map(v => (
                <button key={v.k} onClick={() => setParam("view", v.k === "board" ? "" : v.k)}
                  data-testid={`v2j-view-${v.k}`}
                  className={`h-8 px-3 rounded-full text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${
                    view === v.k ? "bg-white text-[#0F1F3A]" : "text-white/80 hover:text-white"
                  }`}>
                  <span className="text-sm leading-none">{v.icon}</span>
                  <span>{v.label}</span>
                </button>
              ))}
            </div>
            <Link to="/admin/jobs?v=classic" data-testid="v2j-classic-toggle"
              className="h-9 px-3 rounded-full bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/25 border border-white/20 inline-flex items-center">
              Use Classic
            </Link>
          </>
        }
      />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-6">
        {/* Toolbar card with filters + status counter chips */}
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-4" data-testid="v2j-toolbar">
          <div className="flex items-center gap-2 flex-wrap pb-1">
            <button onClick={() => setParam("status", "")}
              data-testid="v2j-filter-all"
              className={`flex-shrink-0 flex items-center gap-2 px-3 h-9 rounded-full border-2 transition-colors ${
                !statusFilter ? "bg-[#0F1F3A] border-[#0F1F3A] text-white" : "border-slate-200 text-[#0F1F3A] hover:bg-slate-50"
              }`}>
              <span className="text-xs font-black">All</span>
              <span className={`text-xs font-black ${!statusFilter ? "text-[#00C4CC]" : "text-[#8B93A7]"}`}>{counts._total}</span>
            </button>
            {STATUS_ORDER.map(s => {
              const active = statusFilter === s.k;
              const color = STATUS_COLORS[s.k] || "#8B93A7";
              return (
                <button key={s.k} onClick={() => setParam("status", active ? "" : s.k)}
                  data-testid={`v2j-filter-${s.k}`}
                  className="flex-shrink-0 flex items-center gap-2 px-3 h-9 rounded-full border-2 hover:bg-slate-50 transition-colors"
                  style={{ borderColor: active ? color : color + "44", background: active ? color + "12" : "transparent" }}>
                  <span className="h-2 w-4 rounded-sm flex-shrink-0" style={{ background: color }}/>
                  <span className="text-xs font-bold text-[#0F1F3A]">{s.label}</span>
                  <span className="text-xs font-black" style={{ color }}>{counts[s.k] || 0}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            {propertyFilter && (
              <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#00C4CC]/10 border-2 border-[#00C4CC] text-[#0F1F3A] font-black uppercase tracking-widest text-[10px]"
                data-testid="v2j-property-chip">
                <span>📍 {propertyFilter}</span>
                <button onClick={() => setParam("property", "")}
                  data-testid="v2j-property-chip-clear"
                  className="ml-1 h-5 w-5 rounded-full bg-white hover:bg-red-100 flex items-center justify-center text-slate-600 hover:text-red-700">
                  ×
                </button>
              </div>
            )}
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <input type="text" value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search WO#, property, tenant, tech, title…"
                data-testid="v2j-search"
                className="w-full h-9 pl-9 pr-3 rounded-full bg-slate-50 border border-slate-200 text-sm text-[#0F1F3A] placeholder:text-[#8B93A7] focus:outline-none focus:border-[#00C4CC] focus:bg-white"/>
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B93A7] text-sm">🔍</span>
            </div>

            {/* Company / Landlord filter — auto-derived from the loaded jobs. */}
            <select value={companyFilter}
              onChange={e => setParam("company", e.target.value)}
              data-testid="v2j-filter-company"
              className="h-9 px-3 pr-8 rounded-full bg-slate-50 border border-slate-200 text-xs font-bold text-[#0F1F3A] focus:outline-none focus:border-[#00C4CC] max-w-[220px] truncate">
              <option value="">All companies ({companyOptions.length})</option>
              {companyOptions.map(([name, n]) => (
                <option key={name} value={name}>{name} · {n}</option>
              ))}
            </select>

            {/* Tech / Vendor filter — same idea for assignees. */}
            <select value={techFilter}
              onChange={e => setParam("tech", e.target.value)}
              data-testid="v2j-filter-tech"
              className="h-9 px-3 pr-8 rounded-full bg-slate-50 border border-slate-200 text-xs font-bold text-[#0F1F3A] focus:outline-none focus:border-[#00C4CC] max-w-[200px] truncate">
              <option value="">All techs ({techOptions.length})</option>
              {techOptions.map(([name, n]) => (
                <option key={name} value={name}>{name} · {n}</option>
              ))}
            </select>

            {(companyFilter || techFilter || portfolioFilter || priorityFilter) && (
              <button onClick={() => {
                setParam("company", ""); setParam("tech", "");
                setParam("portfolio", ""); setParam("priority", "");
              }}
                data-testid="v2j-filters-clear"
                className="h-9 px-3 rounded-full border border-slate-200 text-[10px] font-black uppercase tracking-widest text-[#8B93A7] hover:bg-red-50 hover:text-red-700 hover:border-red-300">
                × Clear all
              </button>
            )}

            <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
              Showing <b className="text-[#0F1F3A]">{filtered.length}</b> · view: {view}
            </div>
          </div>

          {/* Priority pill chips — same-glance triage. */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap" data-testid="v2j-priority-row">
            <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">Priority</span>
            {[
              { k: "",            label: "All",         color: "#8B93A7", count: jobs.length },
              { k: "urgent",      label: "🔥 Urgent",   color: "#E64C5A", count: priorityCounts.urgent },
              { k: "life_safety", label: "🚨 Life Safety", color: "#B91C1C", count: priorityCounts.life_safety },
              { k: "normal",      label: "Normal",      color: "#64748B", count: priorityCounts.normal },
            ].map(p => {
              const active = priorityFilter === p.k;
              return (
                <button key={p.k || "all"} onClick={() => setParam("priority", active ? "" : p.k)}
                  data-testid={`v2j-priority-${p.k || "all"}`}
                  className="flex-shrink-0 flex items-center gap-2 px-3 h-8 rounded-full border-2 transition-colors"
                  style={{
                    borderColor: active ? p.color : p.color + "44",
                    background: active ? p.color + "18" : "transparent",
                  }}>
                  <span className="text-[11px] font-bold text-[#0F1F3A]">{p.label}</span>
                  <span className="text-[10px] font-black" style={{ color: p.color }}>{p.count}</span>
                </button>
              );
            })}
          </div>

          {/* Portfolio pill chips — auto-derived from portfolio_slug/name/color. */}
          {portfolioOptions.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="v2j-portfolio-row">
              <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">Portfolio</span>
              <button onClick={() => setParam("portfolio", "")}
                data-testid="v2j-portfolio-all"
                className={`flex-shrink-0 flex items-center gap-2 px-3 h-8 rounded-full border-2 transition-colors ${
                  !portfolioFilter ? "bg-[#0F1F3A] border-[#0F1F3A] text-white" : "border-slate-200 text-[#0F1F3A] hover:bg-slate-50"
                }`}>
                <span className="text-[11px] font-bold">All</span>
                <span className={`text-[10px] font-black ${!portfolioFilter ? "text-[#00C4CC]" : "text-[#8B93A7]"}`}>{jobs.length}</span>
              </button>
              {portfolioOptions.slice(0, 10).map(([slug, meta]) => {
                const active = portfolioFilter === slug;
                return (
                  <button key={slug} onClick={() => setParam("portfolio", active ? "" : slug)}
                    data-testid={`v2j-portfolio-${slug}`}
                    className="flex-shrink-0 flex items-center gap-2 px-3 h-8 rounded-full border-2 transition-colors"
                    style={{
                      borderColor: active ? meta.color : meta.color + "44",
                      background: active ? meta.color + "18" : "transparent",
                    }}>
                    <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: meta.color }}/>
                    <span className="text-[11px] font-bold text-[#0F1F3A] max-w-[160px] truncate">{meta.name}</span>
                    <span className="text-[10px] font-black" style={{ color: meta.color }}>{meta.count}</span>
                  </button>
                );
              })}
              {portfolioOptions.length > 10 && (
                <span className="text-[10px] text-[#8B93A7] font-medium">+ {portfolioOptions.length - 10} more…</span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="mt-4 text-center py-12 text-[#8B93A7] font-bold">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="mt-4 bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] p-12 text-center">
            <div className="text-4xl mb-2">🎯</div>
            <div className="text-lg font-black text-[#0F1F3A]">No matches</div>
            <div className="text-sm text-[#8B93A7] mt-1">Try clearing filters or search terms</div>
          </div>
        ) : view === "list" ? (
          <ListView jobs={filtered} workers={workers} onChanged={loadJobs}/>
        ) : (
          <BoardView groups={groupedByStatus} workers={workers} onChanged={loadJobs}/>
        )}
      </div>
    </AdminShell>
  );
}

/* ─── Board view (kanban) ────────────────────────── */
function BoardView({ groups, workers, onChanged }) {
  return (
    <div className="mt-4 flex gap-3 overflow-x-auto pb-3" data-testid="v2j-board">
      {STATUS_ORDER.map(s => (
        <BoardColumn key={s.k} status={s.k} label={s.label} jobs={groups[s.k] || []}
          workers={workers} onChanged={onChanged}/>
      ))}
    </div>
  );
}

function BoardColumn({ status, label, jobs, workers, onChanged }) {
  const color = STATUS_COLORS[status] || "#8B93A7";
  return (
    <div className="flex-shrink-0 w-[300px]" data-testid={`v2j-col-${status}`}>
      <div className="bg-white rounded-t-2xl shadow-[0_4px_20px_rgb(0,0,0,0.08)] px-3 py-2.5 flex items-center gap-2 border-b-4" style={{ borderColor: color }}>
        <span className="h-2 w-4 rounded-sm flex-shrink-0" style={{ background: color }}/>
        <span className="text-[11px] font-black uppercase tracking-widest text-[#0F1F3A] flex-1 truncate">{label}</span>
        <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{ background: color + "22", color }}>{jobs.length}</span>
      </div>
      <div className="bg-slate-50/60 rounded-b-2xl p-2 space-y-2 min-h-[240px] max-h-[70vh] overflow-y-auto">
        {jobs.length === 0 ? (
          <div className="text-center text-[10px] font-medium text-[#8B93A7] py-8 border-2 border-dashed border-slate-200 rounded-xl">No WOs</div>
        ) : (
          jobs.map(j => <BoardCard key={j.id} job={j} accent={color} workers={workers} onChanged={onChanged}/>)
        )}
      </div>
    </div>
  );
}

function BoardCard({ job, accent, workers, onChanged }) {
  const pri = job.priority;
  const priColor = pri === "urgent" || pri === "critical" ? "#E64C5A"
                : pri === "high" ? "#F97316"
                : pri === "med" || pri === "medium" ? "#F5B93F"
                : "#8B93A7";
  const isUrgent = pri === "urgent" || pri === "critical" || job.habitability;
  return (
    <div className="relative group bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow border border-slate-100"
      data-testid={`v2j-card-${job.id}`}>
      {/* Card body links to the WO detail page */}
      <Link to={`/admin/jobs/${job.id}?v=v2`} className="block">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] truncate">
            #{job.work_order_number || job.id.slice(0, 6)}
          </span>
          {pri && (
            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0"
              style={{ background: priColor + "22", color: priColor }}>
              {pri}
            </span>
          )}
          {isUrgent && <span className="text-xs" title="Urgent">🔥</span>}
          {job.source && (
            <span className="ml-auto text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-100 text-[#8B93A7]">
              {job.source}
            </span>
          )}
        </div>
        <div className="text-sm font-black text-[#0F1F3A] leading-tight line-clamp-2">{job.title || "(no title)"}</div>
        {/* Description preview — the 2-line teaser is often the actual "what
            is this WO?" data (esp. for CU imports whose title is just the
            WO#). Skipped when it's a placeholder or matches the title. */}
        {job.description && (() => {
          const d = String(job.description).trim();
          if (!d) return null;
          if (d.toLowerCase().startsWith("imported from clickup task")) return null;
          if (d.toLowerCase() === (job.title || "").toLowerCase()) return null;
          return (
            <div className="text-[11px] text-slate-600 mt-1 leading-snug line-clamp-2">
              {d}
            </div>
          );
        })()}
        <div className="text-[11px] font-medium text-[#8B93A7] mt-1 flex items-center gap-1">
          <span>📍</span>
          <span className="truncate">
            {job.property_address || "—"}
            {job.unit && <> · #{job.unit}</>}
          </span>
        </div>
      </Link>
      {/* Pin-to-Sweeper affordance — stops link propagation. Sits on its
          own row so admins can dispatch to a truck without opening the WO. */}
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-2"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <div className="h-6 w-6 rounded-full flex items-center justify-center text-white font-black text-[10px] flex-shrink-0"
          style={{ background: job.assigned_to_name ? "#3B82F6" : "#94A3B8" }}
          title={job.assigned_to_name || "Unassigned"}>
          {job.assigned_to_name ? initials(job.assigned_to_name) : "—"}
        </div>
        <span className="text-[10px] font-medium text-[#8B93A7] flex-1 truncate">
          {job.assigned_to_name || <span className="text-[#F97316] font-bold">Unassigned</span>}
        </span>
        {job.sweeper_pin ? (
          <span data-testid={`v2j-pin-badge-${job.id}`}
            className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0"
            title={`Pinned to ${job.sweeper_pin.truck_label || "sweeper"} · ${job.sweeper_pin.date || ""}`}>
            <PushPin weight="fill" size={9}/> Pinned
          </span>
        ) : (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            data-testid={`v2j-pin-wrapper-${job.id}`}>
            <PinToSweeperButton jobIds={[job.id]} workers={workers} onPinned={onChanged}/>
          </span>
        )}
        <span className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7] flex-shrink-0">{fmtDate(job.created_at)}</span>
      </div>
    </div>
  );
}

/* ─── List view (dense table) ─────────────────────── */
function ListView({ jobs, workers, onChanged }) {
  return (
    <div className="mt-4 bg-white rounded-2xl shadow-[0_8px_30px_rgb(15,31,58,0.10)] overflow-hidden" data-testid="v2j-list">
      <div className="hidden md:grid grid-cols-[80px_1fr_180px_120px_100px_80px_100px_80px] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        {["#", "Work Order", "Property", "Status", "Tech", "Priority", "Pin", "Age"].map(h => (
          <div key={h} className="text-[9px] font-black uppercase tracking-widest text-[#8B93A7]">{h}</div>
        ))}
      </div>
      <div className="divide-y divide-slate-50 max-h-[70vh] overflow-y-auto">
        {jobs.map(j => (
          <div key={j.id} data-testid={`v2j-row-${j.id}`}
            className="grid md:grid-cols-[80px_1fr_180px_120px_100px_80px_100px_80px] grid-cols-1 gap-2 px-4 py-3 items-center hover:bg-slate-50">
            <Link to={`/admin/jobs/${j.id}?v=v2`} className="contents">
              <div className="text-xs font-black text-[#8B93A7] hidden md:block">#{j.work_order_number || j.id.slice(0, 6)}</div>
              <div className="min-w-0">
                <div className="text-sm font-black text-[#0F1F3A] truncate">
                  <span className="md:hidden text-[#8B93A7] mr-1.5 font-black">#{j.work_order_number || j.id.slice(0, 6)}</span>
                  {j.title || "(no title)"}
                </div>
                <div className="text-[10px] font-medium text-[#8B93A7] truncate">{j.tenant_name || j.customer_name || "—"}{j.source && ` · ${j.source}`}</div>
              </div>
              <div className="text-xs font-medium text-[#0F1F3A] truncate">{j.property_address || "—"}{j.unit && ` · #${j.unit}`}</div>
              <div><StatusFlag status={j.status} size="xs"/></div>
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="h-6 w-6 rounded-full flex items-center justify-center text-white font-black text-[10px] flex-shrink-0"
                  style={{ background: j.assigned_to_name ? "#3B82F6" : "#F97316" }}>
                  {j.assigned_to_name ? initials(j.assigned_to_name) : "?"}
                </div>
                <span className="text-xs font-medium text-[#0F1F3A] truncate">{j.assigned_to_name || "Unassigned"}</span>
              </div>
              <div className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">{j.priority || "—"}</div>
            </Link>
            {/* Pin cell — must escape the Link so its dropdown clicks aren't
                intercepted as navigation. */}
            <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              {j.sweeper_pin ? (
                <span data-testid={`v2j-list-pinned-${j.id}`}
                  className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  <PushPin weight="fill" size={9}/> {j.sweeper_pin.truck_label || "Pinned"}
                </span>
              ) : (
                <PinToSweeperButton jobIds={[j.id]} workers={workers} onPinned={onChanged}/>
              )}
            </div>
            <Link to={`/admin/jobs/${j.id}?v=v2`}
              className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
              {fmtDate(j.created_at)}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

// Normalize address strings for property-scoped WO filter — Property page
// links might send "101 N 58th St, Philadelphia, PA" while a job carries
// "101 N 58th St." Casing, trailing dot, commas, and doubled spaces
// shouldn't hide otherwise-matching WOs.
function normAddr(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
function matchesPropertyAddress(rowAddr, filterAddr) {
  const a = normAddr(rowAddr);
  if (!filterAddr) return true;
  // Take the house-number + street portion BEFORE normalizing so commas
  // still exist for the split. "101 N 58th St, Philadelphia, PA" →
  // "101 n 58th st".
  const bHead = normAddr(String(filterAddr).split(",")[0]);
  if (!bHead) return true;
  if (a === bHead) return true;
  return a.startsWith(bHead);
}
function fmtDate(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    const now = new Date();
    const daysAgo = Math.floor((now - d) / 86400000);
    if (daysAgo === 0) return "today";
    if (daysAgo === 1) return "1d";
    if (daysAgo < 30) return `${daysAgo}d`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}
